import React from "react"

import { Bike } from "models"
import * as BikeApi from "features/bike/api"

export const bikeRegisteredEnum = {
	initialStep: "initialStep",
	codeValidationStep: "codeValidationStep",
	bikeRegistrationStep: "bikeRegistrationStep",
}

export type DefaultStateProps = {
	step: string
	bikeInfo: BikeApi.GetBikePasswordAnswer
	bikeId: string
	bike?: Bike
}

export enum BikeRegistrationActionType {
	INITIAL_STEP = "INITIAL_STEP",
	CODE_VALIDATION_STEP = "CODE_VALIDATION_STEP",
	BIKE_REGISTRATION_STEP = "BIKE_REGISTRATION_STEP",
}

export type BikeRegistrationAction =
	| { type: "INITIAL_STEP" }
	| {
			type: "CODE_VALIDATION_STEP"
			bikeInfo: BikeApi.GetBikePasswordAnswer | null
			bikeId: string
			bike: null
	  }
	| {
			type: "BIKE_REGISTRATION_STEP"
			bikeInfo: null
			bikeId: string
			bike: Bike
	  }

export const defaultState: DefaultStateProps = {
	step: bikeRegisteredEnum.initialStep,
	bikeInfo: null,
	bikeId: null,
	bike: null,
}

type BikeRegistrationProcessContextInterface = {
	bikeRegistrationState: DefaultStateProps
	bikeRegistrationDispatch: React.Dispatch<BikeRegistrationAction>
}

const BikeRegistrationProcessContext = React.createContext<
	BikeRegistrationProcessContextInterface | undefined
>(undefined)

const bikeRegistrationProcessReducer = (
	bikeRegistrationState: DefaultStateProps,
	action: BikeRegistrationAction
) => {
	switch (action.type) {
		// Initial step: the user fills the bike id field
		case "INITIAL_STEP": {
			return defaultState
		}

		// Code validation step: the user has received a code by email
		// and he/she fills the activation code field
		case "CODE_VALIDATION_STEP": {
			return {
				step: bikeRegisteredEnum.codeValidationStep,
				bikeInfo: action.bikeInfo,
				bikeId: action.bikeId,
				bike: null,
			}
		}

		// Bike registration step: the user hasn't received a code by email
		// because his/her email is unknown. He/She is redirected to the
		// registration page (bike form).
		case "BIKE_REGISTRATION_STEP": {
			return {
				step: bikeRegisteredEnum.bikeRegistrationStep,
				bikeInfo: action.bikeInfo,
				bikeId: action.bikeId,
				bike: action.bike,
			}
		}
	}
}

/**********************
 ** Context provider **
 **********************/

// prettier-ignore
const BikeRegistrationProcessProvider = ({ children }: { children: React.ReactNode }) => {
	const [bikeRegistrationState, bikeRegistrationDispatch] = React.useReducer(
		bikeRegistrationProcessReducer,
		defaultState
	)
	return (
		<BikeRegistrationProcessContext.Provider
			value={{ bikeRegistrationState, bikeRegistrationDispatch }}
		>
			{children}
		</BikeRegistrationProcessContext.Provider>
	)
}

/**********************
 ** Context consumer **
 **********************/
// The context consumer uses the render props pattern
// to directly provide a consumer component with the value to consume

type ChildrenRenderProps = {
	children: (props: Record<string, unknown>) => React.ReactNode
}

// prettier-ignore
const BikeRegistrationProcessConsumer = ({ children }: ChildrenRenderProps) => (
	<BikeRegistrationProcessContext.Consumer>
		{({ bikeRegistrationState }) => children({ bikeRegistrationState })}
	</BikeRegistrationProcessContext.Consumer>
)

/**********************
 ** Context hook **
 **********************/

const useBikeRegistrationProcess = () => {
	const context = React.useContext(BikeRegistrationProcessContext)
	if (context === undefined) {
		throw new Error(
			"useBikeRegistrationProcess must be used within a BikeRegistrationProcessProvider"
		)
	}
	return context
}

export {
	useBikeRegistrationProcess,
	BikeRegistrationProcessProvider,
	BikeRegistrationProcessConsumer,
}
